import { useState } from "react";
import { Lock, Coins, ChevronRight, X, Sparkles, Gift, Users, Trophy, Target, Calendar, Flag, MessageSquare, Bot, Wallet } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

// Feature definitions with coin costs and descriptions
export const UNLOCKABLE_FEATURES = {
  find_golfers: {
    id: "find_golfers",
    name: "Find Golfers",
    description: "Connect with golfers in your area based on handicap, play style, and availability. Build your golf network.",
    icon: Users,
    coinCost: 50,
    freeUnlock: { type: "rounds", count: 3 },
    category: "connect"
  },
  open_tee_times: {
    id: "open_tee_times",
    name: "Open Tee Times",
    description: "Post your upcoming rounds and find partners. Join other golfers' open tee times.",
    icon: Calendar,
    coinCost: 50,
    freeUnlock: { type: "rounds", count: 3 },
    category: "connect"
  },
  golf_buddy: {
    id: "golf_buddy",
    name: "Golf Buddy",
    description: "Send buddy requests to golfers you want to play with regularly. Build your foursome.",
    icon: Users,
    coinCost: 25,
    freeUnlock: { type: "rounds", count: 3 },
    category: "connect"
  },
  social_feed: {
    id: "social_feed",
    name: "Social Feed",
    description: "Share your rounds, photos, and golf moments with the Par Seeker community.",
    icon: MessageSquare,
    coinCost: 50,
    freeUnlock: { type: "rounds", count: 3 },
    category: "connect"
  },
  match_play: {
    id: "match_play",
    name: "Match Play",
    description: "Challenge golfers to head-to-head match play competitions with handicap adjustments.",
    icon: Trophy,
    coinCost: 75,
    freeUnlock: { type: "rounds", count: 5 },
    category: "compete"
  },
  skins_games: {
    id: "skins_games",
    name: "Skins Games",
    description: "Join or create skins games with real prize pools. Win holes, win money.",
    icon: Wallet,
    coinCost: 75,
    freeUnlock: { type: "rounds", count: 5 },
    category: "compete"
  },
  stroke_play: {
    id: "stroke_play",
    name: "Stroke Play",
    description: "Compete in stroke play tournaments against multiple players.",
    icon: Flag,
    coinCost: 75,
    freeUnlock: { type: "rounds", count: 5 },
    category: "compete"
  },
  golf_outings: {
    id: "golf_outings",
    name: "Golf Outings",
    description: "Organize large golf events with teams, formats, and live leaderboards.",
    icon: Flag,
    coinCost: 100,
    freeUnlock: { type: "rounds", count: 5 },
    category: "compete"
  },
  ai_assistant: {
    id: "ai_assistant",
    name: "RonRo AI",
    description: "Get personalized golf tips, course strategies, and game analysis from our AI assistant.",
    icon: Bot,
    coinCost: 100,
    freeUnlock: { type: "rounds", count: 10 },
    category: "elevate"
  },
  advanced_stats: {
    id: "advanced_stats",
    name: "Advanced Stats",
    description: "Deep dive into your game with detailed statistics, trends, and improvement tracking.",
    icon: Target,
    coinCost: 100,
    freeUnlock: { type: "rounds", count: 10 },
    category: "elevate"
  }
};

// Ways to earn coins
export const COIN_EARNING_METHODS = [
  { action: "Log a round", coins: 10, icon: Target },
  { action: "Invite friend (verified)", coins: 50, icon: Gift },
  { action: "Complete profile", coins: 25, icon: Users },
  { action: "First handicap (3 rounds)", coins: 100, icon: Trophy },
  { action: "Daily login streak", coins: 5, icon: Sparkles }
];

const UnlockFeatureModal = ({ 
  feature, 
  isOpen, 
  onClose, 
  userCoins = 0,
  onBuyCoins,
  onUnlockWithCoins 
}) => {
  const navigate = useNavigate();
  const [showEarnMethods, setShowEarnMethods] = useState(false);
  
  if (!isOpen || !feature) return null;

  const featureConfig = UNLOCKABLE_FEATURES[feature];
  if (!featureConfig) return null;

  const Icon = featureConfig.icon;
  const canAfford = userCoins >= featureConfig.coinCost;

  const handleUnlock = () => {
    if (canAfford && onUnlockWithCoins) {
      onUnlockWithCoins(feature, featureConfig.coinCost);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-sm bg-[#1a1a1a] rounded-2xl border border-gray-800 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative p-6 pb-4 bg-gradient-to-b from-gray-800/50 to-transparent">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
          
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">{featureConfig.name}</h2>
            <p className="text-gray-400 text-sm">Feature Locked</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Feature Description */}
          <div className="p-4 bg-gray-800/50 rounded-xl mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                {featureConfig.description}
              </p>
            </div>
          </div>

          {/* Unlock Cost */}
          <div className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-4">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              <span className="text-white font-medium">Unlock Cost</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-2xl font-bold text-amber-400">{featureConfig.coinCost}</span>
              <span className="text-amber-400/70 text-sm">coins</span>
            </div>
          </div>

          {/* Your Balance */}
          <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg mb-4">
            <span className="text-gray-400 text-sm">Your Balance</span>
            <div className="flex items-center gap-1">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className={`font-bold ${canAfford ? 'text-emerald-400' : 'text-gray-400'}`}>
                {userCoins}
              </span>
            </div>
          </div>

          {/* Free Unlock Path */}
          <div className="text-center text-gray-500 text-xs mb-4">
            <span>or unlock free after </span>
            <span className="text-emerald-400 font-medium">
              {featureConfig.freeUnlock.count} rounds
            </span>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {canAfford ? (
              <Button
                onClick={handleUnlock}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold rounded-xl"
              >
                <Coins className="w-5 h-5 mr-2" />
                Unlock Now ({featureConfig.coinCost} Coins)
              </Button>
            ) : (
              <Button
                onClick={() => onBuyCoins?.()}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold rounded-xl"
              >
                <Coins className="w-5 h-5 mr-2" />
                Buy Coins
              </Button>
            )}

            <Button
              onClick={() => setShowEarnMethods(!showEarnMethods)}
              variant="outline"
              className="w-full h-12 border-gray-700 text-gray-300 hover:bg-gray-800 rounded-xl"
            >
              <Gift className="w-5 h-5 mr-2 text-emerald-400" />
              Keep Earning
              <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${showEarnMethods ? 'rotate-90' : ''}`} />
            </Button>
          </div>

          {/* Earn Methods Dropdown */}
          {showEarnMethods && (
            <div className="mt-4 p-4 bg-gray-800/50 rounded-xl space-y-3 animate-fade-in">
              <p className="text-sm text-gray-400 mb-3">Ways to earn Golf Coins:</p>
              {COIN_EARNING_METHODS.map((method, index) => {
                const MethodIcon = method.icon;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MethodIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-300 text-sm">{method.action}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-amber-400 font-medium text-sm">+{method.coins}</span>
                      <Coins className="w-3 h-3 text-amber-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnlockFeatureModal;
